import { HttpError } from './http-error'
import type { ShenAiTokenResponse } from './types'

/**
 * The second vendor-specific route (`POST /v1/public/shenai/token`, docs/decisions.md D-32).
 *
 * Its own client rather than a method on `ScanClient` or on `MindsetClient`, for the reason the
 * Mindset one gives about itself: the three scan routes are the flow every vendor shares, and each
 * of these exists only because of how ONE vendor licenses a run. The two are not even the same
 * shape - Mindset exchanges a challenge the SDK raised, Shen mints a credential the SDK never saw -
 * which is why neither could have been a parameter on the other.
 */
export interface ShenAiClient {
  /**
   * Mints the short-lived token the SDK initializes with.
   *
   * **Nothing is sent.** The vendor's admin key lives in the service, and the browser has nothing
   * to contribute to the mint - so the body is empty and the patient's scoped access token is the
   * whole of the request. That token is what makes the mint spendable only by someone holding a
   * live scan link.
   */
  mintToken(accessToken: string): Promise<{ token: string; userId: string }>
}

export function createShenAiClient(baseUrl: string): ShenAiClient {
  return {
    async mintToken(accessToken) {
      const response = await fetch(`${baseUrl}/v1/public/shenai/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      })

      // No 404 branch, which is the route's shape rather than an omission: it decides nothing about
      // whether the assessment is still usable, because GET /v1/public/scan and POST
      // /v1/public/scan/start both ran before an SDK ever needed a credential.
      if (!response.ok) {
        throw new HttpError(response.status, `shenai token mint failed with ${response.status}`)
      }

      const minted = (await response.json()) as ShenAiTokenResponse

      if (!minted?.token) {
        // A 200 with nothing usable in it. Said out loud because the alternative is the vendor's
        // `initialize` answering INVALID_API_KEY for a token that was never there, which reads like
        // a credential problem and is not one.
        throw new HttpError(response.status, 'shenai token mint answered 200 with no token')
      }

      console.log('[shenai] minted a scan token', { expiresAt: minted.expiresAt })

      return { token: minted.token, userId: minted.userId }
    },
  }
}
